import { useState, useEffect } from 'react';
import { Plus, Trash2, Cpu, X } from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import { techStackApi, TECH_CATEGORIES, type TechStackEntry, type TechCategory } from '@/services/techStackApi';
import { PageEmptyState } from '@/components/onboarding/PageEmptyState';
import { cn } from '@/lib/utils';

const CATEGORY_COLORS: Record<TechCategory, string> = {
  language: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
  framework: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
  infrastructure: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800',
  database: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
  testing: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800',
  cicd: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  other: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
};

export default function TechStackTab() {
  const [entries, setEntries] = useState<TechStackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [category, setCategory] = useState<TechCategory>('framework');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    techStackApi.list().then(setEntries).catch(() => setEntries([])).finally(() => setLoading(false));
  }, []);

  async function handleAdd() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const entry = await techStackApi.add({
        name: name.trim(),
        version: version.trim() || undefined,
        category,
        notes: notes.trim() || undefined,
      });
      setEntries((prev) => [entry, ...prev]);
      setName('');
      setVersion('');
      setNotes('');
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    await techStackApi.remove(id);
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }

  // Group entries by category
  const grouped = entries.reduce<Record<string, TechStackEntry[]>>((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  if (loading) {
    return <LoadingState size="lg" />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Tech Stack</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Declare the technologies your team uses. This feeds into check suggestions and CLAUDE.md generation.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
        >
          <Plus className="w-4 h-4" /> Add Technology
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="mb-6 p-4 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Add Technology</h3>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. React, Python, DynamoDB"
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Version</label>
              <input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="e.g. 19, 3.12, latest"
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TechCategory)}
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {TECH_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">Notes</label>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context"
                className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleAdd}
              disabled={saving || !name.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              {saving && <Spinner size="sm" tone="current" />}
              Add
            </button>
          </div>
        </div>
      )}

      {/* Entries grouped by category */}
      {entries.length === 0 && !showForm ? (
        <PageEmptyState
          icon={Cpu}
          title="No technologies declared yet"
          description="Add the frameworks, languages, and tools your team uses — or index a repository and let the scanner detect them."
          action="indexRepo"
        />
      ) : (
        <div className="space-y-6">
          {TECH_CATEGORIES.filter((c) => grouped[c.value]?.length).map((cat) => (
            <div key={cat.value}>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                {cat.label} ({grouped[cat.value].length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {grouped[cat.value].map((entry) => (
                  <div
                    key={entry.id}
                    className={cn(
                      'group flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium',
                      CATEGORY_COLORS[entry.category]
                    )}
                  >
                    <span>{entry.name}</span>
                    {entry.version && (
                      <span className="text-xs opacity-70">{entry.version}</span>
                    )}
                    {entry.notes && (
                      <span className="text-xs opacity-60 max-w-32 truncate" title={entry.notes}>
                        — {entry.notes}
                      </span>
                    )}
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-current hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
